/**
 * LoadingSkeleton Component
 * 
 * Provides loading skeleton placeholders for content that is being loaded.
 * Used to improve perceived performance and provide visual feedback during loading states.
 */

export interface LoadingSkeletonProps {
  /** Width of the skeleton (CSS value) */
  width?: string;
  /** Height of the skeleton (CSS value) */
  height?: string;
  /** Border radius (CSS value) */
  borderRadius?: string;
  /** Additional CSS classes */
  className?: string;
  /** Aspect ratio (e.g., '3/4' for photos) */
  aspectRatio?: string;
}

/**
 * LoadingSkeleton component - Animated placeholder for loading content
 */
export function LoadingSkeleton({
  width = '100%',
  height,
  borderRadius = '0.5rem',
  className = '',
  aspectRatio,
}: LoadingSkeletonProps) {
  const style: React.CSSProperties = {
    width,
    ...(height && { height }),
    borderRadius,
    ...(aspectRatio && { aspectRatio }),
  };

  return (
    <div
      className={`animate-pulse bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] ${className}`}
      style={style}
      role="status"
      aria-label="Loading..."
    >
      <span className="sr-only">Loading...</span>
    </div>
  );
}

/**
 * FrameCardSkeleton - Skeleton for frame layout cards
 */
export function FrameCardSkeleton() {
  return (
    <div className="w-full rounded-lg overflow-hidden border-4 border-transparent">
      <LoadingSkeleton aspectRatio="3/4" className="w-full" />
      <div className="p-3 bg-white">
        <LoadingSkeleton height="1rem" width="80%" className="mb-2" />
        <LoadingSkeleton height="0.75rem" width="60%" />
      </div>
    </div>
  );
}

/**
 * PhotoSkeleton - Skeleton for photo thumbnails
 */
export function PhotoSkeleton() {
  return (
    <LoadingSkeleton aspectRatio="3/4" className="w-full rounded-lg" />
  );
}

<style>{`
  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  .animate-pulse {
    animation: shimmer 2s ease-in-out infinite;
  }
`}</style>
